/**
 * ClassAlias - class name aliasing for backward compatibility during deserialization.
 */

import { ClassRegistry } from '../../systems/ClassRegistry.js';

import type { Constructor } from '../../systems/ClassRegistry.js';

export class GlobalObjectRegistry {
  private static objects: Map<string, object> = new Map();
  private static factories: Map<string, () => object> = new Map();
  public static NAME = '$global';

  constructor() {}

  public static registerObject(name: string, obj: object): void {
    this.objects.set(name, obj);
  }

  public static registerFactory(name: string, factory: () => object): void {
    this.factories.set(name, factory);
  }

  private static ensureInstantiated(name: string): object | undefined {
    if (this.objects.has(name)) {
      return this.objects.get(name);
    }
    const factory = this.factories.get(name);
    if (factory) {
      const obj = factory();
      this.objects.set(name, obj);
      return obj;
    }
    return undefined;
  }

  public static isGlobalObject(obj: object): boolean {
    // First check already instantiated objects
    for (const registeredObj of this.objects.values()) {
      if (registeredObj === obj) {
        return true;
      }
    }
    // Check factories and instantiate if the object matches
    for (const name of this.factories.keys()) {
      if (!this.objects.has(name)) {
        const instantiated = this.ensureInstantiated(name);
        if (instantiated === obj) {
          return true;
        }
      }
    }
    return false;
  }

  public static getObjectByName(name: string): any {
    return this.ensureInstantiated(name);
  }
}


const classAliases = new Map<Constructor, string>();
const reverseClassAliases = new Map<string, Constructor>();

export function getClassSerializeName(obj: object | undefined): string | null {
  if (!obj || typeof obj !== 'object') return null;
  if (GlobalObjectRegistry.isGlobalObject(obj)) {
    return GlobalObjectRegistry.NAME;
  }
  return classAliases.get(obj.constructor as Constructor) ?? ClassRegistry.getClassName(obj) ?? null;
}

export function getClassForDeserialization(cls: string | undefined): Constructor | null {
  if (!cls) return null;

  // try catch to avoid errors from ClassRegistry, as we can't tell if `cls` is always valid - class name can change, storage data can be manually edited, etc.
  try {
    return reverseClassAliases.get(cls) ?? ClassRegistry.getClass(cls);
  } catch {
    return null;
  }
}

export function registerClassAlias(cls: Constructor, alias: string): void {
  if (classAliases.has(cls)) {
    throw new Error(`Class alias conflict: ${cls.name} is already aliased to ${classAliases.get(cls)}`);
  }
  if (typeof cls.constructor !== 'function') {
    throw new Error('Cannot register serialization for non-class type');
  }
  if (reverseClassAliases.has(alias)) {
    throw new Error(`Class alias conflict: ${alias} is already aliased to ${reverseClassAliases.get(alias)?.name}`);
  }
  classAliases.set(cls, alias);
  reverseClassAliases.set(alias, cls);
}
